/**
 * DebugService - Singleton service for capturing call debug information
 *
 * This service centralizes debug info capture. It captures SDP, ICE candidates,
 * and connection states when calls end. Also provides debug info download
 * functionality.
 */

import type {
  CallDebugInfo,
  CallDebugCaptureData,
  DebugServiceInterface,
  SoftphoneEvent,
} from './index.types';
import { buildCallQualitySummary } from 'Modules/softphone/components/SoftphoneDevTools/utils/buildCallQualitySummary';
import { EventService } from 'Modules/softphone/services/EventService';
import { WebRTCService } from 'Modules/softphone/services/WebRTCService';
import { useSoftphoneStore } from 'Modules/softphone/store';
import { createLogger } from 'Modules/softphone/utils/logger';
import VoipMonitorService from 'Services/voipMonitorService';
import {
  buildDebugFilename,
  buildDebugReport,
  buildInteractionFilename,
  buildInteractionReport,
  downloadTextFile,
} from './buildDebugReport';
import { MAX_RECENT_CALLS } from './index.constants';

export type {
  CallDebugInfo,
  CallDebugCaptureData,
  DebugServiceInterface,
} from './index.types';

const logger = createLogger('DebugService');

/**
 * Extract ICE candidates from SDP
 */
const extractIceCandidatesFromSdp = (sdp: string | null): string[] => {
  if (!sdp) return [];
  const lines = sdp.split('\r\n');
  return lines.filter((line) => line.startsWith('a=candidate:'));
};

/** How far before a call's start an unattributed event still counts as its own. */
const EVENT_WINDOW_PRE_ROLL_MS = 5000;

/**
 * Pick the events that belong to one call.
 *
 * Identity first: an event that names a call belongs to that call and to no
 * other. This is what keeps a warm transfer's two legs — or a conference's five
 * — from each claiming the whole timeline, which is what the old
 * timestamp-window-only approach did.
 *
 * The window survives only as the fallback for events that legitimately belong
 * to no call: SIP registration traffic and device switches.
 */
const selectEventsForCall = ({
  events,
  callId,
  conferenceId,
  startTime,
  endTime,
}: {
  events: SoftphoneEvent[];
  callId: string;
  conferenceId?: string | null;
  startTime: string | null;
  endTime: string | null;
}): SoftphoneEvent[] => {
  const from = startTime
    ? new Date(startTime).getTime() - EVENT_WINDOW_PRE_ROLL_MS
    : 0;
  const to = endTime ? new Date(endTime).getTime() : Date.now();

  return events.filter((event) => {
    if (event.callId) {
      return event.callId === callId;
    }
    if (event.conferenceId) {
      return !!conferenceId && event.conferenceId === conferenceId;
    }

    const at = new Date(event.timestamp).getTime();
    return at >= from && at <= to;
  });
};

export class DebugService implements DebugServiceInterface {
  private static instance: DebugService;
  private recentCalls: Map<string, CallDebugInfo> = new Map();

  // Events now carry their own callId, so there is nothing to correlate here.
  // The previous constructor subscribed to EventService with an empty listener
  // and kept an unread `callEvents` map — both removed with the workaround they
  // were written for.
  private constructor() {}

  static getInstance(): DebugService {
    if (!DebugService.instance) {
      DebugService.instance = new DebugService();
    }
    return DebugService.instance;
  }

  /**
   * Capture debug info when a call ends
   * Called by CallManager.handleSessionStateChange() when state is 'Terminated'
   *
   * @param callId - The ID of the call
   * @param callData - Call data needed for debug capture
   */
  capture(callId: string, callData: CallDebugCaptureData): void {
    // Skip if already captured
    if (this.recentCalls.has(callId)) {
      logger.debug(`Debug info already captured for call: ${callId}`);
      return;
    }

    const webrtcService = WebRTCService.getInstance();
    const pc = webrtcService.getPeerConnection(callId);

    const localSdp = pc?.localDescription?.sdp || null;
    const remoteSdp = pc?.remoteDescription?.sdp || null;

    // Get current device selections from store
    const state = useSoftphoneStore.getState();
    const selectedMicrophone = state.selectedMicrophone;
    const selectedSpeaker = state.selectedSpeaker;
    const selectedRinger = state.selectedRinger;

    // Get device labels
    const getMicrophoneLabel = () => {
      if (!selectedMicrophone) return null;
      const device = state.microphones.find(
        (d) => d.deviceId === selectedMicrophone
      );
      return device?.label || selectedMicrophone;
    };

    const getSpeakerLabel = () => {
      if (!selectedSpeaker) return null;
      const device = state.speakers.find((d) => d.deviceId === selectedSpeaker);
      return device?.label || selectedSpeaker;
    };

    const getRingerLabel = () => {
      if (!selectedRinger) return null;
      const device = state.ringers.find((d) => d.deviceId === selectedRinger);
      return device?.label || selectedRinger;
    };

    const callEvents = selectEventsForCall({
      events: EventService.getInstance().getAll(),
      callId,
      conferenceId: callData.conference?.roomGuid,
      startTime: callData.startTime,
      endTime: callData.endTime,
    });

    const deviceChanges = callEvents.filter((event) => event.type === 'DEVICE');

    const debugInfo: CallDebugInfo = {
      callId,
      phoneNumber: callData.phoneNumber,
      direction: callData.direction,
      state: callData.state,
      startTime: callData.startTime,
      answerTime: callData.answerTime,
      endTime: callData.endTime,
      localSdp,
      remoteSdp,
      iceConnectionState: pc?.iceConnectionState || null,
      connectionState: pc?.connectionState || null,
      signalingState: pc?.signalingState || null,
      iceCandidates: {
        local: extractIceCandidatesFromSdp(localSdp),
        remote: extractIceCandidatesFromSdp(remoteSdp),
      },
      devices: {
        microphone: getMicrophoneLabel(),
        speaker: getSpeakerLabel(),
        ringer: getRingerLabel(),
      },
      deviceChanges, // Microphone/speaker/ringer switches during this call
      events: callEvents, // Only events during this call
      scenario: callData.scenario ?? 'direct',
      endedBy: callData.endedBy ?? null,
      interactionId: callData.interactionId ?? callId,
      relatedCallIds: callData.relatedCallIds,
      transfer: callData.transfer,
      conference: callData.conference,
      quality:
        VoipMonitorService.getMonitoredCallId() === callId
          ? (buildCallQualitySummary(VoipMonitorService.getMetricsHistory()) ??
            undefined)
          : undefined,
    };

    this.recentCalls.set(callId, debugInfo);
    logger.info(`Captured debug info for ended call: ${callId}`);

    // Prune old calls to maintain max limit
    this.pruneOldCalls();
  }

  /**
   * Record what the far end said about a REFER.
   *
   * The NOTIFY arrives after the leg has already been captured, so this
   * upgrades an existing record rather than creating one. Without it a
   * transfer's outcome stays at 'referred' — sent, nothing heard back — which
   * is honest but not the whole story once the far end has answered.
   *
   * The NOTIFY frequently never arrives here: sip.js 0.21.2 discards in-dialog
   * NOTIFYs once the session is no longer `Established`, and a completed
   * attended transfer races that NOTIFY against the BYE. A transfer that
   * succeeded is then recorded as 'referred'. Fixing the observation point is a
   * SIP-stack concern, not a panel one.
   *
   * TODO: CUU2-3564 — observe the REFER NOTIFY above the dialog (UserAgent
   * delegate or transport tap) so a completed transfer reaches this method.
   */
  recordTransferOutcome(
    callId: string,
    { response }: { response: string }
  ): void {
    const info = this.recentCalls.get(callId);
    if (!info?.transfer) {
      return;
    }

    const referResponses = [...(info.transfer.referResponses ?? []), response];
    const isFinalSuccess = /\s2\d{2}\s/.test(` ${response} `);
    const isFailure = /\s[456]\d{2}\s/.test(` ${response} `);

    this.recentCalls.set(callId, {
      ...info,
      transfer: {
        ...info.transfer,
        referResponses,
        outcome: isFinalSuccess
          ? 'accepted'
          : isFailure
          ? 'failed'
          : info.transfer.outcome,
        failureReason: isFailure ? response : info.transfer.failureReason,
      },
    });
  }

  /**
   * Get debug info from cache only (ended calls)
   *
   * @param callId - The ID of the call
   * @returns CallDebugInfo or null if not found
   */
  getCached(callId: string): CallDebugInfo | null {
    return this.recentCalls.get(callId) || null;
  }

  /**
   * Get debug info with fallback to active calls
   * First checks cached ended calls, then falls back to active calls
   *
   * @param callId - The ID of the call
   * @returns CallDebugInfo or null if not found
   */
  get(callId: string): CallDebugInfo | null {
    // Check cached ended calls first
    const cachedInfo = this.recentCalls.get(callId);
    if (cachedInfo) {
      return cachedInfo;
    }

    // Fall back to active calls
    const state = useSoftphoneStore.getState();
    const call = state.activeCalls.get(callId);
    if (!call) {
      return null;
    }

    const webrtcService = WebRTCService.getInstance();
    const pc = webrtcService.getPeerConnection(callId);
    const localSdp = pc?.localDescription?.sdp || null;
    const remoteSdp = pc?.remoteDescription?.sdp || null;

    // Get device labels for active call
    const getMicrophoneLabel = () => {
      if (!state.selectedMicrophone) return null;
      const device = state.microphones.find(
        (d) => d.deviceId === state.selectedMicrophone
      );
      return device?.label || state.selectedMicrophone;
    };

    const getSpeakerLabel = () => {
      if (!state.selectedSpeaker) return null;
      const device = state.speakers.find(
        (d) => d.deviceId === state.selectedSpeaker
      );
      return device?.label || state.selectedSpeaker;
    };

    const getRingerLabel = () => {
      if (!state.selectedRinger) return null;
      const device = state.ringers.find(
        (d) => d.deviceId === state.selectedRinger
      );
      return device?.label || state.selectedRinger;
    };

    // Live timeline for the still-active call. This path used to return no
    // events at all, so downloading diagnostics mid-call produced a file with
    // no timeline — exactly the case a developer hits while a conference is
    // still up and they want evidence now.
    const liveEvents = selectEventsForCall({
      events: EventService.getInstance().getAll(),
      callId: call.id,
      conferenceId: call.conferenceId,
      startTime: call.startTime,
      endTime: call.endTime,
    });
    const deviceChanges = liveEvents.filter((event) => event.type === 'DEVICE');

    return {
      callId: call.id,
      phoneNumber: call.phoneNumber,
      direction: call.direction,
      state: call.state,
      startTime: call.startTime,
      answerTime: call.answerTime,
      endTime: call.endTime,
      localSdp,
      remoteSdp,
      iceConnectionState: pc?.iceConnectionState || null,
      connectionState: pc?.connectionState || null,
      signalingState: pc?.signalingState || null,
      iceCandidates: {
        local: extractIceCandidatesFromSdp(localSdp),
        remote: extractIceCandidatesFromSdp(remoteSdp),
      },
      devices: {
        microphone: getMicrophoneLabel(),
        speaker: getSpeakerLabel(),
        ringer: getRingerLabel(),
      },
      deviceChanges,
      events: liveEvents,
      scenario: call.conferenceId ? 'conference' : 'direct',
      endedBy: call.endedBy ?? null,
      interactionId: call.conferenceId ?? call.id,
      conference: call.conferenceId
        ? {
            roomGuid: call.conferenceId,
            participantsAreLocallyInferred: true,
          }
        : undefined,
    };
  }

  /**
   * Get all cached debug entries
   *
   * @returns Array of CallDebugInfo for recent calls
   */
  getAll(): CallDebugInfo[] {
    return Array.from(this.recentCalls.values());
  }

  /**
   * Clear all cached debug entries
   */
  clear(): void {
    this.recentCalls.clear();
    logger.info('Cleared debug history');
  }

  /**
   * Download debug info as text file
   *
   * @param callId - The ID of the call
   */
  download(callId: string): void {
    const debugInfo = this.get(callId);
    if (!debugInfo) {
      logger.error(`Call not found for download: ${callId}`);
      return;
    }

    const filename = buildDebugFilename(debugInfo);
    downloadTextFile(filename, buildDebugReport(debugInfo));

    logger.info(`Downloaded debug info: ${filename}`);
  }

  /**
   * Download every leg of one interaction as a single file.
   *
   * A warm transfer or a conference is not diagnosable one leg at a time, and
   * the whole premise of the tool is a single artefact that can be attached to
   * a support ticket.
   */
  downloadInteraction(interactionId: string): void {
    const legs = this.getInteraction(interactionId);
    if (legs.length === 0) {
      logger.error(`Interaction not found for download: ${interactionId}`);
      return;
    }

    const filename = buildInteractionFilename(legs);
    downloadTextFile(filename, buildInteractionReport(legs));

    logger.info(`Downloaded interaction debug info: ${filename}`);
  }

  /**
   * Every cached leg belonging to one interaction, oldest first.
   */
  getInteraction(interactionId: string): CallDebugInfo[] {
    return Array.from(this.recentCalls.values())
      .filter(
        (info) => (info.interactionId ?? info.callId) === interactionId
      )
      .sort((a, b) => {
        const left = a.startTime ? new Date(a.startTime).getTime() : 0;
        const right = b.startTime ? new Date(b.startTime).getTime() : 0;
        return left - right;
      });
  }

  /**
   * Prune old calls to maintain max limit
   * Removes oldest entries when limit is exceeded
   */
  private pruneOldCalls(): void {
    while (this.recentCalls.size > MAX_RECENT_CALLS) {
      const firstKey = this.recentCalls.keys().next().value as
        | string
        | undefined;
      if (firstKey) {
        this.recentCalls.delete(firstKey);
        logger.debug(`Pruned old debug info for call: ${firstKey}`);
      }
    }
  }

  /**
   * Get the current count of stored debug entries
   * Useful for testing
   */
  get count(): number {
    return this.recentCalls.size;
  }

  /**
   * Reset the singleton instance (for testing purposes)
   */
  static resetInstance(): void {
    if (DebugService.instance) {
      DebugService.instance.recentCalls.clear();
    }
    DebugService.instance = undefined as unknown as DebugService;
  }
}
